import * as React from 'react';
import { Route, Routes } from 'react-router-dom';

import { useAuthStore } from '@Stores';
import { useMountEffect, useAppTheme } from '@Hooks';

import {
    ErrorBoundary,
    Layout,
    Loading,
    ModalContainer,
    Modals, NotificationContainer
} from '@Components';
import './Scss/App.scss';

const Dashboard = React.lazy(() => import('@Pages/Dashboard/Dashboard'));
const NoAccess = React.lazy(() => import('@Pages/NoAccess/NoAccess'));
const Administration = React.lazy(() => import('@Pages/Administration/Administration'));
const NotFound = React.lazy(() => import('../src/Pages/NotFound/NotFound'));
const ServiceBeneficiaryAgreement = React.lazy(() => import('@Pages/ServiceBeneficiaryAgreement/ServiceBeneficiaryAgreement'));
const ServiceBeneficiaryAgreementDetails = React.lazy(() => import('@Pages/ServiceBeneficiaryAgreement/ServiceBeneficiaryAgreementDetails/ServiceBeneficiaryAgreementDetails'));
const LocalUnitAgreement = React.lazy(() => import('@Pages/LocalUnitAgreement/LocalUnitAgreement'));
const LocalUnitAgreementDetails = React.lazy(() => import('@Pages/LocalUnitAgreement/LocalUnitAgreementDetails/LocalUnitAgreementDetails'));
const AccrualsInflow = React.lazy(() => import('@Pages/AccrualsInflow/AccrualsInflow'));
const AccrualsOutflow = React.lazy(() => import('@Pages/AccrualsOutflow/AccrualsOutflow'));
const PeopleVerification = React.lazy(() => import('@Pages/PeopleVerification/PeopleVerification'));
const PeopleVerificationTable = React.lazy(() => import('@Pages/PeopleVerification/PeopleVerificationTable/PeopleVerificationTable'));
const Reports = React.lazy(() => import('@Pages/Reports/Reports'));
const InvoicingInflow = React.lazy(() => import("@Pages/InvoicingInflow/InvoicingInflow"));
const InvoicingOutflow = React.lazy(() => import( "@Pages/InvoicingOutflow/InvoicingOutflow"));
const CostImport = React.lazy(() => import( "@Pages/CostImport/CostImport"));
const AppsInput = React.lazy(() => import( "@Pages/AppsInput/AppsInput"));
const AppsInputDetails = React.lazy(() => import('@Pages/AppsInput/AppsInputDetails/AppsInputDetails'));
const AppsOutflow = React.lazy(() => import( "@Pages/AppsOutflow/AppsOutflow"));

type AppProps = {
	callback: () => void;
};

function App({ callback }: AppProps) {
    const appSecurityModel = useAuthStore(state => state.appSecurityModel);
    const loadAppSecurityModel = useAuthStore(state => state.loadAppSecurityModel);

    useAppTheme();

    useMountEffect(() => {
        // signalRService.initialize();
        callback();
    });

    React.useEffect(() => { loadAppSecurityModel() }, [loadAppSecurityModel]);

    if (!appSecurityModel) return <Loading fillContainer />;

    const canReadAdministration = appSecurityModel.hasPrincipalApplicationAdministrationRead || appSecurityModel.hasPrincipalAnyMetadataRead;

    return (
        <ErrorBoundary>
            <NotificationContainer />
            <ModalContainer />
            <Modals/>
            <Layout>
                <React.Suspense fallback={<Loading fillContainer />}>
                    <Routes>
                        <Route path="/">
                            {!appSecurityModel.hasPrincipalAnyPermission &&
                              <Route path="*" element={<NoAccess />} />
                            }

                            {appSecurityModel.hasPrincipalAnyPermission &&
                              <Route index element={<Dashboard />} />
                            }
                            {!appSecurityModel.hasPrincipalAnyPermission &&
                              <Route index element={<NoAccess />} />
                            }

                          {appSecurityModel.hasPrincipalAnyPermission &&
                            <>
                                <Route path="local-unit-agreement">
                                    <Route index element={<LocalUnitAgreement />} />
                                    <Route path=":agreementId/*" element={<LocalUnitAgreementDetails />} />
                                </Route>
                                <Route path="service-beneficiary-agreement">
                                    <Route index element={<ServiceBeneficiaryAgreement />} />
                                    <Route path=":agreementId/*" element={<ServiceBeneficiaryAgreementDetails />} />
                                </Route>
                                <Route path="accruals-inflow">
                                    <Route index element={<AccrualsInflow />} />
                                </Route>
                                <Route path="accruals-outflow">
                                    <Route index element={<AccrualsOutflow />} />
                                </Route>
                                <Route path="reports">
                                    <Route index element={<Reports />} />
                                </Route>
                                <Route path="invoicing-inflow">
                                    <Route index element={<InvoicingInflow />} />
                                </Route>
                                <Route path="invoicing-outflow">
                                    <Route index element={<InvoicingOutflow />} />
                                </Route>
                                <Route path="cost-import">
                                    <Route index element={<CostImport />} />
                                </Route>
                                <Route path="inflow">
                                    <Route index element={<AppsInput />} />
                                    <Route path=":inputId/*" element={<AppsInputDetails />} />
                                </Route>
                                <Route path="outflow">
                                    <Route index element={<AppsOutflow />} />
                                    {/*<Route path=":outflowId/*" element={<AppsOutflowDetails />} />*/}
                                </Route>
                                <Route path="people-verification">
                                    <Route index element={<PeopleVerification />} />
                                    <Route path=":reportId/*" element={<PeopleVerificationTable />} />
                                </Route>
                            </>
                          }

                            {canReadAdministration &&
                              <Route path="administration/*" element={<Administration appSecurityModel={appSecurityModel} />} />
                            }

                            {appSecurityModel.hasPrincipalAnyPermission &&
                              <Route path="*" element={<NotFound />} />
                            }
                        </Route>
                    </Routes>
                </React.Suspense>
            </Layout>
        </ErrorBoundary>
    );
}

export default App;
